import { styled } from "@linaria/react";
import React, { useMemo } from "react";
import type { Setup } from "../../../../../api/useSetups";
import { useCanUserDoOnProject } from "../../../../canUserDo";
import TooltipV2 from "@ui/TooltipV2";
import { H3 } from "@ui/Text";
import { InformationLine, PencilLine } from "@ui/IconV2/react";
import OverlayWrapper from "@ui/TooltipV2/OverlayWrapper";
import { colors, fonts } from "@ui/Theme";
import t from "../../../../../i18n";
import { css } from "@linaria/core";
import Button from "@ui/Button";
import { useModals } from "@ui/lib/hooks/useModal";
import Modal from "@ui/Modal";
import UpdateAlgorithmModalForm from "./UpdateAlgorithmModalForm";
import { useAlgorithmPresets } from "../../../../../api/useAlgorithmPresets";
import { indexBy } from "diagnostics-lib/iterable";
import { translationLocale } from "../../../../../i18n/locale";

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  font: ${fonts.body1};
`;

const CustomOverlay = styled.div`
  max-width: 326px;
`;
const Icon = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  color: ${colors.carbon800};
`;

const StyledTitle = css`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledVersion = css`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Settings = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledParameters = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: ${colors.carbon700};

  ul {
    font: ${fonts.body2};
  }
`;

const color900Css = css`
  color: ${colors.carbon900};
`;

const LinkButton = styled(Button)`
  height: auto;
`;

const Version = ({ preset }: { preset: string | null }) => (
  <p className={color900Css}>
    Version{translationLocale === "fr" ? "\u00A0" : ""}:&nbsp;
    <strong>{preset || "<unknown algorithm>"}</strong>
  </p>
);

interface P {
  projectId: string;
  setup: Setup;
}

const ProjectAlgorithm = ({ projectId, setup }: P) => {
  const { modal, closeModal, openModal } = useModals<"edit">();
  const canUserEditSetup = useCanUserDoOnProject("PROJECT_SETUP", projectId);
  const algorithmVersions = useAlgorithmPresets();
  const { diagnosisPreset: setupVersionKey, diagnosisParameters } = setup;
  const indexParametersBykey = indexBy(diagnosisParameters, "key");

  const algorithmVersion = algorithmVersions.find(f => f.key === setupVersionKey);

  /**
   * Fetch algorithm presets
   */

  const preset = algorithmVersion?.translateKey || null;
  const updatedParameters = useMemo(
    () =>
      algorithmVersion
        ? algorithmVersion.parameters.flatMap(param => {
            const value = indexParametersBykey.get(param.key)?.value;
            if (value === undefined || param.defaultValue === value) return [];
            return [{ key: param.translateKey, value }];
          })
        : [],
    [algorithmVersion, indexParametersBykey],
  );

  return (
    <>
      <Section>
        <div className={StyledTitle}>
          <H3 className={color900Css}>{t("setup.diagnosis.algorithm.title")}</H3>
          <TooltipV2
            placement="top"
            overlay={
              <OverlayWrapper
                text={<CustomOverlay>{t("setup.diagnosis.algorithm.tooltip.text")}</CustomOverlay>}
              />
            }
          >
            <Icon>
              <InformationLine size="l" />
            </Icon>
          </TooltipV2>
        </div>

        <Settings>
          <div className={StyledVersion}>
            <Version preset={preset} />
            {canUserEditSetup ? (
              <LinkButton
                variant="link"
                onClick={() => openModal("edit")}
                leftIcon={<PencilLine />}
              >
                {t("setup.diagnosis.algorithm.link_button.edit")}
              </LinkButton>
            ) : null}
          </div>

          {updatedParameters.length > 0 ? (
            <div className={StyledParameters}>
              <p>{t("setup.diagnosis.algorithm.parameters.edit.text")}&nbsp;:</p>
              <ul>
                {updatedParameters.map(m => (
                  <li key={m.key}>
                    {m.key} : {m.value}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </Settings>
      </Section>
      <Modal
        title={t("setup.diagnosis.algorithm.modal.title")}
        isOpen={modal === "edit"}
        onRequestClose={closeModal}
        size="l"
      >
        <UpdateAlgorithmModalForm
          projectId={projectId}
          setup={setup}
          onRequestClose={closeModal}
          okButtonName={t("setup.diagnosis.algorithm.modal.ok_button_name")}
        />
      </Modal>
    </>
  );
};

export default React.memo(ProjectAlgorithm);
